import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CalendarClock, MapPin, DollarSign, CheckCircle2 } from "lucide-react";

const EMPTY = {
  email: "", participant_name: "", address: "", date_of_birth: "", participant_email: "",
  guardian_name: "", guardian_relation: "Parent", guardian_relation_other: "",
  guardian_email: "", guardian_phone: "", emergency_contact: "",
  billing_party: "Guardian", billing_party_other: "",
  participant_status: "", support_notes: "", goals: "", how_heard: "",
  photo_release: false, liability_release: false, comments: "",
};

const inputCls = "mt-1 w-full h-11 px-3 rounded-lg border border-[#d6cecf] focus:border-[#920e12] focus:ring-2 focus:ring-[#ffdedf] outline-none";
const areaCls = "mt-1 w-full px-3 py-2 rounded-lg border border-[#d6cecf] focus:border-[#920e12] focus:ring-2 focus:ring-[#ffdedf] outline-none";

function Field({ label, required, children, testid, hint }) {
  return (
    <div data-testid={testid}>
      <Label className="text-[#1a1617] font-semibold">
        {label} {required && <span className="text-[#920e12]">*</span>}
      </Label>
      {hint && <p className="text-xs text-[#545454] mb-1">{hint}</p>}
      {children}
    </div>
  );
}

export default function Registration() {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.photo_release || !f.liability_release) {
      toast.error("Please agree to both the photo/video and liability releases.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        email: f.email.trim(),
        participant_name: f.participant_name.trim(),
        address: f.address.trim(),
        date_of_birth: f.date_of_birth.trim(),
        participant_email: f.participant_email.trim(),
        guardian_name: f.guardian_name.trim(),
        guardian_relation: f.guardian_relation === "Other" ? (f.guardian_relation_other.trim() || "Other") : f.guardian_relation,
        guardian_email: f.guardian_email.trim(),
        guardian_phone: f.guardian_phone.trim(),
        emergency_contact: f.emergency_contact.trim(),
        billing_party: f.billing_party === "Other" ? (f.billing_party_other.trim() || "Other") : f.billing_party,
        participant_status: f.participant_status || null,
        support_notes: f.support_notes.trim(),
        goals: f.goals.trim(),
        how_heard: f.how_heard.trim(),
        photo_release: f.photo_release,
        liability_release: f.liability_release,
        comments: f.comments.trim(),
      };
      await api.post("/registrations", payload);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="registration-success" className="max-w-2xl mx-auto px-4 py-24 text-center">
        <CheckCircle2 size={56} className="mx-auto text-[#15803d]" />
        <h1 className="mt-4 font-display text-4xl text-[#550001]">Registration received!</h1>
        <p className="mt-3 text-[#545454]">
          Thank you for registering, {f.participant_name || "friend"}. Our team will be in touch about next steps and payment.
          Questions? Email <a className="text-[#920e12] underline" href="mailto:liz@theimprovaneermethod.com">liz@theimprovaneermethod.com</a>.
        </p>
        <Button onClick={() => { setF(EMPTY); setDone(false); }} className="mt-6 bg-[#920e12] hover:bg-[#550001] text-white" data-testid="registration-again-btn">
          Register another participant
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="registration-page">
      <PageHeader eyebrow="Class Registration" title="Register for a class"
        image="https://customer-assets-agu9un31.emergentagent.net/job_improv-prompt-engine/artifacts/a1mylrfv_Nick%20Big%20Show%202024.webp"
        subtitle="Improv Cincinnati / Improvaneer Method In-Person Classes. Fully inclusive, ALL abilities welcome." />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { Icon: MapPin, t: "Hoffner Lodge", d: "4122 Hamilton Ave, Cincinnati, OH 45223" },
            { Icon: CalendarClock, t: "Tuesdays 6:30–8:00pm EST", d: "Sep–Nov 2026 · 10 sessions" },
            { Icon: DollarSign, t: "$275 / session", d: "Invoicing available on request" },
          ].map(({ Icon, t, d }) => (
            <div key={t} className="bg-white rounded-2xl border border-[#dfd8da] p-4">
              <Icon size={20} className="text-[#920e12]" />
              <div className="mt-2 font-bold text-sm text-[#1a1617]">{t}</div>
              <div className="text-xs text-[#545454]">{d}</div>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl border border-[#dfd8da] p-6 sm:p-8 space-y-6" data-testid="registration-form">
          <p className="text-sm text-[#545454]">Fields marked <span className="text-[#920e12] font-bold">*</span> are required.</p>

          <Field label="Your email" required testid="reg-email">
            <input type="email" required value={f.email} onChange={set("email")} className={inputCls} data-testid="reg-email-input" />
          </Field>

          <Field label="Participant name" required testid="reg-participant-name">
            <input required value={f.participant_name} onChange={set("participant_name")} className={inputCls} data-testid="reg-participant-name-input" />
          </Field>

          <Field label="Address" required testid="reg-address">
            <input required value={f.address} onChange={set("address")} className={inputCls} data-testid="reg-address-input" />
          </Field>

          <Field label="Date of birth" required testid="reg-dob">
            <input type="date" required value={f.date_of_birth} onChange={set("date_of_birth")} className={inputCls} data-testid="reg-dob-input" />
          </Field>

          <Field label="Participant email" required testid="reg-participant-email">
            <input type="email" required value={f.participant_email} onChange={set("participant_email")} className={inputCls} data-testid="reg-participant-email-input" />
          </Field>

          <div className="border-t border-[#eee] pt-6">
            <h2 className="font-display text-2xl text-[#550001] mb-4">Guardian &amp; contacts</h2>
            <div className="space-y-6">
              <Field label="Guardian name" required testid="reg-guardian-name">
                <input required value={f.guardian_name} onChange={set("guardian_name")} className={inputCls} data-testid="reg-guardian-name-input" />
              </Field>

              <Field label="Guardian relation" required testid="reg-guardian-relation">
                <select value={f.guardian_relation} onChange={(e) => setF({ ...f, guardian_relation: e.target.value, guardian_relation_other: e.target.value === "Other" ? f.guardian_relation_other : "" })} className={inputCls} data-testid="reg-guardian-relation-input">
                  <option>Parent</option><option>Caregiver</option><option>Other</option>
                </select>
                {f.guardian_relation === "Other" && (
                  <input value={f.guardian_relation_other} onChange={set("guardian_relation_other")} placeholder="Please specify" className={`${inputCls} mt-2`} data-testid="reg-guardian-relation-other" />
                )}
              </Field>

              <Field label="Guardian email" required testid="reg-guardian-email">
                <input type="email" required value={f.guardian_email} onChange={set("guardian_email")} className={inputCls} data-testid="reg-guardian-email-input" />
              </Field>

              <Field label="Guardian phone number" required testid="reg-guardian-phone">
                <input type="tel" required value={f.guardian_phone} onChange={set("guardian_phone")} className={inputCls} data-testid="reg-guardian-phone-input" />
              </Field>

              <Field label="Emergency contact" required hint="Please include: Name, Relation, Phone #" testid="reg-emergency">
                <input required value={f.emergency_contact} onChange={set("emergency_contact")} className={inputCls} data-testid="reg-emergency-input" />
              </Field>

              <Field label="Responsible billing party" required testid="reg-billing">
                <select value={f.billing_party} onChange={(e) => setF({ ...f, billing_party: e.target.value, billing_party_other: e.target.value === "Other" ? f.billing_party_other : "" })} className={inputCls} data-testid="reg-billing-input">
                  <option>Guardian</option><option>Participant</option><option>Other</option>
                </select>
                {f.billing_party === "Other" && (
                  <input value={f.billing_party_other} onChange={set("billing_party_other")} placeholder="Please include name and email" className={`${inputCls} mt-2`} data-testid="reg-billing-other" />
                )}
              </Field>
            </div>
          </div>

          <div className="border-t border-[#eee] pt-6">
            <h2 className="font-display text-2xl text-[#550001] mb-4">Getting to know you</h2>
            <div className="space-y-6">
              <Field label="New or returning participant" testid="reg-status">
                <select value={f.participant_status} onChange={set("participant_status")} className={inputCls} data-testid="reg-status-input">
                  <option value="">Select…</option><option>New</option><option>Returning</option>
                </select>
              </Field>

              <Field label="If new, is there anything you believe we should know in order to better support you in class?" testid="reg-support">
                <textarea rows={3} value={f.support_notes} onChange={set("support_notes")} className={areaCls} data-testid="reg-support-input" />
              </Field>

              <Field label="What are your goals for attending this class?" hint="No wrong answers — it could be as simple as wanting to have fun!" testid="reg-goals">
                <textarea rows={3} value={f.goals} onChange={set("goals")} className={areaCls} data-testid="reg-goals-input" />
              </Field>

              <Field label="How did you hear about this class?" testid="reg-how-heard">
                <input value={f.how_heard} onChange={set("how_heard")} className={inputCls} data-testid="reg-how-heard-input" />
              </Field>
            </div>
          </div>

          <div className="border-t border-[#eee] pt-6">
            <h2 className="font-display text-2xl text-[#550001] mb-4">Agreements</h2>
            <div className="space-y-4">
              <label className="flex items-start gap-3 bg-[#f9f6f6] rounded-lg p-4 border border-[#dfd8da] cursor-pointer" data-testid="reg-photo-release">
                <Checkbox checked={f.photo_release} onCheckedChange={(v) => setF({ ...f, photo_release: !!v })} data-testid="reg-photo-release-check" className="mt-0.5" />
                <span className="text-sm text-[#1a1617]">
                  <strong>Photo / video release. *</strong> I agree to give my permission for photo or video of this
                  participant to be used for promotional or training purposes. I understand that no photos or videos
                  will ever be used in an embarrassing or harmful way towards the participant.
                </span>
              </label>

              <label className="flex items-start gap-3 bg-[#f9f6f6] rounded-lg p-4 border border-[#dfd8da] cursor-pointer" data-testid="reg-liability-release">
                <Checkbox checked={f.liability_release} onCheckedChange={(v) => setF({ ...f, liability_release: !!v })} data-testid="reg-liability-release-check" className="mt-0.5" />
                <span className="text-sm text-[#1a1617]">
                  <strong>Liability release. *</strong> I understand that participation may be hazardous for the
                  participant. I assume risk of harm or injury which may occur as a result of participating. I hereby
                  release The Improvaneer Method LLC and its officers, employees, or agents from any liability, costs and
                  damages resulting from this participation. I give consent for The Improvaneer Method LLC to seek
                  emergency treatment if necessary and accept financial responsibility for related costs. If the
                  participant is a minor, I confirm the minor has my consent to participate.
                </span>
              </label>
            </div>
          </div>

          <Field label="Comments / questions" testid="reg-comments">
            <textarea rows={3} value={f.comments} onChange={set("comments")} className={areaCls} data-testid="reg-comments-input" />
          </Field>

          <Button type="submit" disabled={busy} className="w-full h-12 bg-[#920e12] hover:bg-[#550001] text-white font-bold text-base" data-testid="registration-submit">
            {busy ? "Submitting…" : "Submit registration"}
          </Button>
        </form>
      </div>
    </div>
  );
}
